import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import React, { ReactNode } from 'react';
import { Auth } from '../Auth';
import { AuthFooterEnum } from '../Auth/Auth.view';
import { ResetPassword } from '../Auth/ResetPassword';
import { useAuth } from '../../hooks/useAuth';
import { BrowserHistory } from 'history';
import { MediaUploadProvider } from '../../providers/MediaUploadProvider';
import { useUserRole } from '../../hooks/useUserRole';
import { UserAppRole } from '../../models/graphql';

export type AnonymousRouteProps = {
  children?: ReactNode;
};

export type ResetPasswordRouteParams = {
  token: string;
};

export type CustomRouterProps = {
  history: BrowserHistory;
  children: ReactNode;
};

export const UserProtectedRoute = ({ children }: { children: JSX.Element }): JSX.Element => {
  const { isLoggedIn } = useAuth();
  // const location = useLocation();
  if (!isLoggedIn) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login
    // TODO When we only allow logging in through React app, we can use the <Navigate> instead of full page load.
    // TODO This is because we render the legacy login portal for now.
    window.location.href = '/login';
    return <></>;
    //return <Navigate to={'/login'} state={{ from: location }} replace />;
  }

  return <MediaUploadProvider>{children}</MediaUploadProvider>;
};

export const AdminProtectedRoute = ({ children }: { children: JSX.Element }): JSX.Element => {
  const { isLoggedIn } = useAuth();
  const location = useLocation();
  const { appRole } = useUserRole();
  if (!isLoggedIn) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login
    // TODO When we only allow logging in through React app, we can use the <Navigate> instead of full page load.
    // TODO This is because we render the legacy login portal for now.
    window.location.href = '/login';
    return <></>;
    //return <Navigate to={'/login'} state={{ from: location }} replace />;
  }
  if (appRole !== UserAppRole.ADMIN) {
    return <Navigate to={'/'} state={{ from: location }} replace />;
  }

  return children;
};

export const AnonymousRoute = ({ children }: AnonymousRouteProps): JSX.Element => {
  const { isLoggedIn } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // If we're logged in, then we should redirect the user to the redirect ('r') parameter, as they may be visiting
  // a URL like /login?r=post-editor and we don't want to override this.
  // We must ensure never to redirect to the 'r' parameter if they're not logged in, as the login logic will handle
  // that after authenticating the user.
  const redirectParam = isLoggedIn ? searchParams.get('r') : '';
  let loggedInPath = '/' + redirectParam || '';
  return <>{!isLoggedIn ? children : <Navigate to={loggedInPath} replace state={{ from: location }} />}</>;
};

export const ResetPasswordRoute = (): JSX.Element => {
  const { token } = useParams<ResetPasswordRouteParams>();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const email = params.get('email');
  return (
    <Auth footer={AuthFooterEnum.backToLogin}>
      <ResetPassword token={token || ''} email={decodeURI(email || '')} />
    </Auth>
  );
};
